import { useState, useRef, useEffect, useCallback } from "react";
import { useVideoFeed, VIDEO_UI, CAUSE_LABEL } from "@/hooks/useVideoFeed";
import { useVideoProfile } from "@/hooks/useVideoProfile";
import { useCameraZoom } from "@/hooks/useCameraZoom";
import { orderedProfileEntries, profileLabel } from "@/components/VideoHealth";
import { X, Maximize2, Minimize2, GripHorizontal, Plus, Minus, Aperture, Power, RotateCcw, Loader2, VideoOff, Cctv } from "lucide-react";

const LS_KEY = "map_camera_window";
const MIN_W = 260, MIN_H = 170;
const load = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; } };
const save = (v) => { try { localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch { /* ignore */ } };

// Fenêtre caméra flottante (WebRTC) — déplaçable, redimensionnable, plein écran.
// Le flux ne se coupe PAS au déplacement/redimensionnement (l'élément vidéo reste monté).
export default function FloatingCameraWindow({ droneId, onClose }) {
  const vf = useVideoFeed(droneId, { autoStart: true });
  const vprofile = useVideoProfile(droneId, { onReconnect: () => vf.connect() });
  const camZoom = useCameraZoom(droneId, { enabled: !!vf.cfg?.enabled });

  const stored = load();
  const [rect, setRect] = useState(() => stored || { x: Math.max(20, window.innerWidth - 440), y: 90, w: 380, h: 250 });
  const [fs, setFs] = useState(false);
  const winRef = useRef(null);
  const dragRef = useRef(null);

  const videoEnabled = !!vf.cfg?.enabled;
  const videoActive = !!vf.playMode;
  const vui = VIDEO_UI[vf.uiState] || VIDEO_UI.unavailable;
  const active = vprofile.current?.profile;

  useEffect(() => { save(rect); }, [rect]);

  // --- Drag (par le header) ---
  const startDrag = (e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { mode: "drag", cx, cy, x: rect.x, y: rect.y };
    attach();
    if (e.cancelable) e.preventDefault();
  };
  const startResize = (e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { mode: "resize", cx, cy, w: rect.w, h: rect.h };
    attach();
    if (e.cancelable) e.preventDefault();
  };
  const onMove = useCallback((e) => {
    const d = dragRef.current; if (!d) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setRect((r) => {
      if (d.mode === "drag") {
        const x = Math.max(0, Math.min(window.innerWidth - r.w, d.x + (cx - d.cx)));
        const y = Math.max(0, Math.min(window.innerHeight - 40, d.y + (cy - d.cy)));
        return { ...r, x, y };
      }
      const w = Math.max(MIN_W, Math.min(window.innerWidth - r.x, d.w + (cx - d.cx)));
      const h = Math.max(MIN_H, Math.min(window.innerHeight - r.y, d.h + (cy - d.cy)));
      return { ...r, w, h };
    });
    if (e.cancelable) e.preventDefault();
  }, []);
  const stop = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stop);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", stop);
  }, [onMove]);
  const attach = () => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", stop);
  };
  useEffect(() => () => stop(), [stop]);

  // --- Fullscreen ---
  const toggleFs = useCallback(async () => {
    try {
      if (!document.fullscreenElement) await winRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const winStyle = fs ? {} : { left: rect.x, top: rect.y, width: rect.w, height: rect.h };

  return (
    <div ref={winRef} data-testid="map-camera-window"
      className="fixed z-[2000] flex flex-col bg-[#050505] border border-[#3B82F6]/50 rounded-md overflow-hidden shadow-2xl"
      style={winStyle}>
      {/* Header (drag handle) */}
      <div data-testid="map-camera-drag" onMouseDown={startDrag} onTouchStart={startDrag}
        className="shrink-0 flex items-center gap-2 px-2 py-1.5 bg-[#0A0A0A] border-b border-white/10 cursor-move touch-none select-none">
        <GripHorizontal size={12} className="text-white/40" />
        <Cctv size={12} className="text-[#3B82F6]" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-white/80">{droneId}</span>
        {active && <span className="font-mono text-[8px] uppercase tracking-wider text-[#3B82F6] border border-[#3B82F6]/40 rounded-full px-1.5 py-0.5" data-testid="map-camera-profile">{profileLabel(active, vprofile.profiles?.profiles?.[active])}</span>}
        <span className="ml-auto flex items-center gap-1">
          <button data-testid="map-camera-fs" onClick={toggleFs} className="w-6 h-6 flex items-center justify-center rounded-sm hover:bg-white/10 text-white/70">
            {fs ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
          <button data-testid="map-camera-close" onClick={() => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); onClose(); }}
            className="w-6 h-6 flex items-center justify-center rounded-sm hover:bg-[#EF4444]/20 text-white/70 hover:text-[#EF4444]"><X size={13} /></button>
        </span>
      </div>

      {/* Video */}
      <div className="relative flex-1 bg-black min-h-0">
        {videoEnabled ? (
          <video ref={vf.videoRef} data-testid="map-camera-video" autoPlay muted playsInline className="w-full h-full object-cover bg-black" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-[#525252]">Caméra non configurée</div>
        )}

        {videoEnabled && !videoActive && (
          <div className="absolute inset-0 flex items-center justify-center px-4" data-testid="map-camera-overlay">
            <div className="flex flex-col items-center gap-2 bg-black/60 border rounded-md px-4 py-3 backdrop-blur-md text-center" style={{ borderColor: vui.color + "55" }}>
              {vf.connecting || vf.service.pending ? <Loader2 size={20} className="animate-spin text-[#3B82F6]" /> : <VideoOff size={20} style={{ color: vui.color }} />}
              <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: vui.color }}>{vui.label}</span>
              {vf.diagnostic.cause && <div className="font-mono text-[8px] text-[#F59E0B] max-w-[220px]">{CAUSE_LABEL[vf.diagnostic.cause]}</div>}
              {!(vf.connecting || vf.service.pending) && (
                <div className="flex items-center gap-1.5">
                  <button data-testid="map-camera-activate" onClick={() => vf.controlService("start")} className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-wider bg-[#22C55E] text-black rounded-full px-2.5 py-1"><Power size={10} /> Activer</button>
                  <button data-testid="map-camera-retry" onClick={vf.connect} className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-wider border border-white/20 rounded-full px-2.5 py-1"><RotateCcw size={10} /> Réessayer</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Zoom réel (Picamera2) */}
        {videoEnabled && videoActive && (
          <div className="absolute bottom-2 right-2 flex flex-col gap-1" data-testid="map-camera-zoom">
            <button data-testid="map-camera-zoom-in" onClick={camZoom.zoomIn} disabled={camZoom.applying || camZoom.level >= 10}
              className="w-8 h-8 flex items-center justify-center rounded-sm bg-black/60 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 disabled:opacity-30 backdrop-blur-md"><Plus size={14} /></button>
            <div className="text-center bg-black/60 rounded-sm py-0.5 backdrop-blur-md"><span className="font-mono text-[10px] font-bold text-[#2DD4BF]">{(camZoom.level || 1).toFixed(1)}x</span></div>
            <button data-testid="map-camera-zoom-out" onClick={camZoom.zoomOut} disabled={camZoom.applying || camZoom.level <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-sm bg-black/60 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 disabled:opacity-30 backdrop-blur-md"><Minus size={14} /></button>
            <button data-testid="map-camera-zoom-reset" onClick={camZoom.reset} disabled={camZoom.applying}
              className="w-8 h-8 flex items-center justify-center rounded-sm bg-black/60 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 disabled:opacity-30 backdrop-blur-md"><Aperture size={13} /></button>
          </div>
        )}

        {/* Profils + infos (surimpression discrète) */}
        {videoEnabled && (
          <div className="absolute top-2 left-2 flex flex-wrap items-center gap-1.5 max-w-[80%]" data-testid="map-camera-profiles">
            {orderedProfileEntries(vprofile.profiles?.profiles || {}).map(([id, p]) => {
              const on = active === id;
              const busy = vprofile.phase !== "idle" && vprofile.phase !== "error";
              return (
                <button key={id} data-testid={`map-camera-profile-${id}`} onClick={() => vprofile.apply(id)} disabled={busy}
                  title={p.usage || ""}
                  className={`px-2 py-1 rounded-md font-mono text-[9px] font-medium tracking-wide transition-colors disabled:opacity-40 border backdrop-blur-md ${
                    on ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                       : "bg-black/55 text-white/75 border-white/15 hover:bg-white/10 hover:text-white hover:border-white/30"}`}>
                  {profileLabel(id, p)}
                </button>
              );
            })}
            {vprofile.blocked && <span data-testid="map-camera-profile-blocked" className="font-mono text-[9px] text-[#F59E0B] bg-black/60 rounded px-1.5 py-0.5">{vprofile.blocked.reason}</span>}
          </div>
        )}
      </div>

      {/* Resize handle */}
      {!fs && (
        <div data-testid="map-camera-resize" onMouseDown={startResize} onTouchStart={startResize}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize touch-none"
          style={{ background: "linear-gradient(135deg, transparent 50%, rgba(59,130,246,0.6) 50%)" }} />
      )}
    </div>
  );
}
